import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import { pluralize } from "utils/stringUtils"

import { TestTemplateSelectTable } from "./TestTemplateSelectTable"
import TestTemplatesSearchSuggestion from "./TestTemplatesSearchSuggestion"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { TestTemplate } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"

interface TestTemplateSelectDialogProps {
  productId: number
  searchQuery: string | AutocompleteOption
  testTemplates: TestTemplate[]
  isFetching: boolean
  selectedTestTemplate: TestTemplate | null
  onSearch: (searchedItem: string | AutocompleteOption) => void
  onTestTemplateSelect: (testTemplateId: string | null) => void
  onClose: () => void
  onContinue: () => Promise<void>
}

const TestTemplateSelectDialog: FC<TestTemplateSelectDialogProps> = ({
  productId,
  searchQuery,
  testTemplates,
  isFetching,
  selectedTestTemplate,
  onSearch,
  onTestTemplateSelect,
  onClose,
  onContinue,
}) => {
  const searchQueryDisplayValue = typeof searchQuery === "string" ? searchQuery : searchQuery.label
  const testTemplatesCount = testTemplates.length ?? 0
  const hasNoTestTemplates = testTemplatesCount === 0 && !isFetching
  const canContinue = !!selectedTestTemplate

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='lg' data-testid='test-template-select-dialog'>
      <DialogHeader title='Associate Quality Test Template' onClose={onClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <Typography className='mb-6 text-ml font-normal text-gray-900'>
          Search for a Test Template to Associate
        </Typography>
        <Box className='flex flex-col gap-6'>
          <TestTemplatesSearchSuggestion productId={productId} search={searchQuery} onSearch={onSearch} />
          {searchQuery && (
            <>
              <Box className='mb-6 flex align-middle'>
                <Typography className='inline-flex align-middle text-sm leading-6'>
                  <span className='font-semibold text-gray-800'>
                    {testTemplatesCount} test {pluralize(testTemplatesCount, "template", "templates")} found
                  </span>
                  &nbsp;
                  {Boolean(testTemplatesCount) && "Select one from the existing"}
                </Typography>
              </Box>
              {hasNoTestTemplates ? (
                <Box className='flex flex-col items-center justify-center'>
                  <Typography className='text-gray-500'>
                    No test templates found for "{searchQueryDisplayValue}"
                  </Typography>
                </Box>
              ) : (
                <Box className='max-h-[400px] overflow-auto'>
                  <TestTemplateSelectTable
                    testTemplates={testTemplates}
                    selectedTestTemplate={selectedTestTemplate}
                    onTestTemplateSelect={onTestTemplateSelect}
                    isFetching={isFetching}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose} data-testid='cancel-button'>
          Cancel
        </Button>
        <Button variant='primary' onClick={onContinue} disabled={!canContinue} data-testid='continue-button'>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TestTemplateSelectDialog
